import React, { useCallback } from 'react';
import {
  useUpdateTeamAvatarSettingsMutation,
  useUpdateUserAvatarSettingsMutation,
} from '@avatar/graphql/updateAvatarSettings/operation.generated';
import { useCanUpdateTeamVoices } from 'abilities/voices';
import {
  StudioProjectVersion,
  TeamAvatarSettingsAction,
  UserAvatarSettingsAction,
} from '../../../../../server/graphql/__generated.types';
import { useEventsContext } from '../../../../context/events/EventsContext';
import { EventName } from '../../../../services/EventTrackingService';
import { usePromptsContext } from '../../../../context/prompts/PromptsContext';
import { useUserContext } from '../../../../context/user/UserContext';
import { getDuplicateOverrideVariants } from '../../utils/utils';
import AvatarOptions, { AvatarOptionsProps } from '.';
import { toast } from '../../../Toaster';

interface AvatarOptionsWrapperProps
  extends Omit<
    AvatarOptionsProps,
    | 'onToggleFavorite'
    | 'onToggleTeamVoice'
    | 'isTeamAdmin'
    | 'isLoading'
    | 'canUpdateTeamVoice'
  > {
  onRefetch: () => Promise<any>;
  sourceIsVoicesPage?: boolean;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  projectVersion: StudioProjectVersion;
}

const AvatarOptionsWrapper = ({
  id,
  avatar,
  variant,
  onSelect,
  activeAvatarId,
  activeAvatarVariantId,
  onRefetch,
  sourceIsVoicesPage,
  loading,
  setLoading,
  projectVersion,
}: AvatarOptionsWrapperProps) => {
  const [userAvatarSettingsUpdate] = useUpdateUserAvatarSettingsMutation();
  const [teamAvatarSettingsUpdate] = useUpdateTeamAvatarSettingsMutation();
  const [, dispatchPrompt] = usePromptsContext();
  const { data: userQuery } = useUserContext();
  const [eventTracking] = useEventsContext();
  const canUpdateTeamVoices = useCanUpdateTeamVoices();

  const handleUnfavoriteAvatarsRequest = useCallback(() => {
    dispatchPrompt({
      type: 'OpenUnfavoriteAvatarsPrompt',
      payload: {
        avatar,
        variant,
        onRefetch,
        sourceIsVoicesPage,
        projectVersion,
      },
    });
  }, [
    avatar,
    dispatchPrompt,
    onRefetch,
    projectVersion,
    sourceIsVoicesPage,
    variant,
  ]);

  const handleRemoveTeamVoiceRequest = useCallback(() => {
    dispatchPrompt({
      type: 'OpenRemoveTeamVoicePrompt',
      payload: {
        avatar,
        variant,
        onRefetch,
        sourceIsVoicesPage,
        projectVersion,
      },
    });
  }, [
    avatar,
    dispatchPrompt,
    onRefetch,
    projectVersion,
    sourceIsVoicesPage,
    variant,
  ]);

  const handleToggleFavorite = useCallback(async () => {
    if (avatar.accessLevel && avatar.accessLevel === 'LOCKED') {
      dispatchPrompt({
        type: 'OpenVoiceUpgradePrompt',
        payload: {
          upgradeContext: 'upgrade_to_access_all',
          upgradePath: sourceIsVoicesPage
            ? 'Voices > Toggle favorite'
            : 'Studio > Toggle Favorite',
        },
      });
      return;
    }

    let isFavorited = variant && Boolean(variant.userSettings?.favoritedAt);

    const avatarId = String(avatar.id);
    let variantId = variant ? String(variant.id) : undefined;
    const action = isFavorited
      ? UserAvatarSettingsAction.Unfavorite
      : UserAvatarSettingsAction.Favorite;

    let variantIds: Array<number | undefined> = [];
    if (sourceIsVoicesPage) {
      variantIds = getDuplicateOverrideVariants(variant?.id);
      if (variantIds.length > 0) {
        variantId = undefined;
        isFavorited = avatar.variants
          .filter(v => variantIds.find(i => i === v.id))
          .every(v => v.userSettings?.favoritedAt);
      } else {
        variantIds = [variant ? variant.id : -1];
      }
    }

    eventTracking.track({
      type: EventName.AvatarOptions,
      data: {
        avatarId,
        variantId: variantIds.length > 0 ? variantIds.join() : variantId,
        action,
      },
    });

    if (isFavorited) {
      // dispatch modal to confirm unfavorite action
      handleUnfavoriteAvatarsRequest();
    } else {
      if (setLoading) setLoading(true);
      if (!sourceIsVoicesPage) {
        // favorite; eventually this will be moved to another modal
        const update = await userAvatarSettingsUpdate({
          variables: {
            inputs: {
              action: UserAvatarSettingsAction.Favorite,
              avatarId,
              variantId,
            },
          },
        });
        if (!update.data?.updateUserAvatarSettings?.success) {
          toast({
            message:
              update.data?.updateUserAvatarSettings?.message ||
              'something went wrong',
            type: 'error',
          });
        }

        await onRefetch().finally(() => {
          if (setLoading) setLoading(false);
        });

        return;
      }

      await Promise.allSettled(
        variantIds.reduce<Array<Promise<any>>>((acc, value) => {
          if (!value) return acc;

          acc.push(
            userAvatarSettingsUpdate({
              variables: {
                inputs: {
                  action: UserAvatarSettingsAction.Favorite,
                  avatarId,
                  variantId: `${value}`,
                },
              },
            })
          );

          return acc;
        }, [])
      )
        .then(value => {
          value.forEach(data => {
            if (data.status === 'fulfilled') {
              if (!data.value.data?.updateUserAvatarSettings?.success) {
                toast({
                  message:
                    data.value.data?.updateUserAvatarSettings?.message ||
                    'something went wrong',
                  type: 'error',
                });
              }
            }
          });
        })
        .catch(err => {})
        .finally(() => {
          onRefetch().finally(() => {
            if (setLoading) setLoading(false);
          });
        });
    }
  }, [
    avatar.accessLevel,
    avatar.id,
    avatar.variants,
    variant,
    sourceIsVoicesPage,
    eventTracking,
    dispatchPrompt,
    handleUnfavoriteAvatarsRequest,
    setLoading,
    userAvatarSettingsUpdate,
    onRefetch,
  ]);

  const handleToggleTeamVoice = useCallback(async () => {
    const teamId = userQuery?.user.team?.id;
    if (!teamId) return;

    let isTeamVoice = variant && Boolean(variant.teamSettings?.favoritedAt);

    const avatarId = String(avatar.id);
    let variantId = variant ? String(variant.id) : undefined;
    const action = isTeamVoice
      ? TeamAvatarSettingsAction.Unfavorite
      : TeamAvatarSettingsAction.Favorite;

    let variantIds: Array<number | undefined> = [];
    if (sourceIsVoicesPage) {
      variantIds = getDuplicateOverrideVariants(variant?.id);
      if (variantIds.length > 0) {
        variantId = undefined;
        isTeamVoice = avatar.variants
          .filter(v => variantIds.find(i => i === v.id))
          .every(v => v.teamSettings?.favoritedAt);
      } else {
        variantIds = [variant ? variant.id : -1];
      }
    }

    eventTracking.track({
      type: EventName.AvatarOptions,
      data: {
        avatarId,
        variantId: variantIds.length > 0 ? variantIds.join() : variantId,
        action: `Team${action}`,
      },
    });

    if (isTeamVoice) {
      handleRemoveTeamVoiceRequest();
    } else {
      if (setLoading) setLoading(true);
      if (!sourceIsVoicesPage) {
        const update = await teamAvatarSettingsUpdate({
          variables: {
            inputs: {
              teamId,
              action: TeamAvatarSettingsAction.Favorite,
              avatarId,
              variantId,
            },
          },
        });
        if (!update.data?.updateTeamAvatarSettings?.success) {
          toast({
            message:
              update.data?.updateTeamAvatarSettings?.message ||
              'something went wrong',
            type: 'error',
          });
        }

        await onRefetch().finally(() => {
          if (setLoading) setLoading(false);
          if (update.data?.updateTeamAvatarSettings?.success)
            toast({
              message: `${avatar.name} (${variant?.variantType.label}) added to Team Voices`,
            });
        });

        return;
      }
      await Promise.allSettled(
        variantIds.reduce<Array<Promise<any>>>((acc, value) => {
          if (!value) return acc;

          acc.push(
            teamAvatarSettingsUpdate({
              variables: {
                inputs: {
                  teamId,
                  action,
                  avatarId,
                  variantId: `${value}`,
                },
              },
            })
          );

          return acc;
        }, [])
      )
        .then(value => {
          value.forEach(data => {
            if (data.status === 'fulfilled') {
              if (!data.value.data?.updateTeamAvatarSettings?.success) {
                toast({
                  message:
                    data.value.data?.updateTeamAvatarSettings?.message ||
                    'something went wrong',
                  type: 'error',
                });
              } else {
                toast({
                  message: `${avatar.name} (${variant?.variantType.label}) added to Team Voices`,
                });
              }
            }
          });
        })
        .catch(err => {})
        .finally(() => {
          onRefetch().finally(() => {
            if (setLoading) setLoading(false);
          });
        });
    }
  }, [
    userQuery?.user.team?.id,
    variant,
    avatar.id,
    avatar.variants,
    avatar.name,
    sourceIsVoicesPage,
    eventTracking,
    handleRemoveTeamVoiceRequest,
    setLoading,
    teamAvatarSettingsUpdate,
    onRefetch,
  ]);

  return (
    <AvatarOptions
      id={id}
      onToggleFavorite={handleToggleFavorite}
      onToggleTeamVoice={handleToggleTeamVoice}
      onSelect={onSelect}
      avatar={avatar}
      variant={variant}
      activeAvatarId={activeAvatarId}
      activeAvatarVariantId={activeAvatarVariantId}
      sourceIsVoicesPage={sourceIsVoicesPage}
      isLoading={loading}
      canUpdateTeamVoice={canUpdateTeamVoices}
    />
  );
};

export default AvatarOptionsWrapper;
